import React, { useState, useEffect, useCallback, useRef } from "react";
import { apiGet, apiPost } from "../../../services/api";
import { useToast } from "../../common/ToastProvider";
import { useI18n } from "../../../i18n/I18nProvider";
import "./BackupRestore.css";

interface DriveFile {
  name: string; // Used as key in APEXO
  drive_file_id: string;
  drive_link: string;
  size_bytes: number;
  created: string;
}

interface ListResponse {
  files: DriveFile[];
  total: number;
}

type BackupFrequency = 'daily' | 'weekly' | 'biweekly' | 'monthly';

interface BackupPolicy {
  enabled: boolean;
  frequency: BackupFrequency;
  run_hour_utc: number;
  run_minute_utc: number;
  keep_days: number;
  keep_max: number;
  upload_to_drive: boolean;
  last_run_at?: string | null;
  next_run_at?: string | null;
}

const DEFAULT_POLICY: BackupPolicy = {
  enabled: true,
  frequency: 'daily',
  run_hour_utc: 18,
  run_minute_utc: 45,
  keep_days: 30,
  keep_max: 90,
  upload_to_drive: true,
  last_run_at: null,
  next_run_at: null,
};

export default function BackupRestore() {
  const { showToast } = useToast();
  const { t, formatDate: formatLocalizedDate } = useI18n();
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [policy, setPolicy] = useState<BackupPolicy>(DEFAULT_POLICY);
  const [loadingPolicy, setLoadingPolicy] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);

  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Track action states by drive_file_id (fallback: filename)
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});
  const [restoring, setRestoring] = useState<Record<string, boolean>>({});

  // Dialog States
  const [dialogState, setDialogState] = useState<{
    type: 'restore' | 'delete' | 'download' | null,
    file?: DriveFile
  }>({ type: null });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await apiGet("/api/admin/backup/list")) as ListResponse;
      setFiles(data.files || []);
    } catch {
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPolicy = useCallback(async () => {
    setLoadingPolicy(true);
    try {
      const data = (await apiGet("/api/admin/backup/policy")) as Partial<BackupPolicy>;
      setPolicy({
        ...DEFAULT_POLICY,
        ...data,
        frequency:
          data.frequency === 'weekly' ||
          data.frequency === 'biweekly' ||
          data.frequency === 'monthly'
            ? data.frequency
            : 'daily',
      });
    } catch {
      setPolicy(DEFAULT_POLICY);
    } finally {
      setLoadingPolicy(false);
    }
  }, []);

  useEffect(() => {
    loadFiles();
    loadPolicy();
  }, [loadFiles, loadPolicy]);

  const fileKey = (file: DriveFile) => file.drive_file_id || file.name;

  const handleSavePolicy = async () => {
    if (savingPolicy) return;
    setSavingPolicy(true);
    try {
      const payload: BackupPolicy = {
        ...policy,
        run_hour_utc: Math.max(0, Math.min(23, Number(policy.run_hour_utc) || 0)),
        run_minute_utc: Math.max(0, Math.min(59, Number(policy.run_minute_utc) || 0)),
        keep_days: Math.max(1, Number(policy.keep_days) || 1),
        keep_max: Math.max(1, Number(policy.keep_max) || 1),
      };
      const saved = (await apiPost("/api/admin/backup/policy", payload)) as Partial<BackupPolicy>;
      setPolicy({
        ...payload,
        ...saved,
        frequency:
          saved.frequency === 'weekly' ||
          saved.frequency === 'biweekly' ||
          saved.frequency === 'monthly'
            ? saved.frequency
            : payload.frequency,
      });
      showToast({ message: "Backup policy saved.", type: "success" });
    } catch (err: any) {
      showToast({ message: err.message || "Failed to save backup policy.", type: "error" });
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleCreateNew = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const res = (await apiPost("/api/admin/backup/run", {})) as any;
      if (res.ok) {
        showToast({ message: "Backup completed successfully.", type: "success" });
        loadFiles();
      } else throw new Error(res.reason || "Backup failed");
    } catch (e: any) {
      showToast({ message: e.message || "Failed to trigger backup", type: "error" });
    } finally {
      setCreating(false);
    }
  };

  const handleUploadClick = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const value = String(reader.result || "");
          const base64 = value.includes(",") ? value.split(",")[1] || "" : value;
          resolve(base64);
        };
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
      await apiPost("/api/admin/backup/upload", {
        filename: file.name,
        file_base64: fileBase64,
      });
      showToast({ message: "Backup uploaded successfully.", type: "success" });
      loadFiles();
    } catch (err: any) {
      showToast({ message: err.message || "Failed to upload backup.", type: "error" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRestore = async (file: DriveFile) => {
    setDialogState({ type: null });
    const key = fileKey(file);
    if (restoring[key]) return;
    setRestoring(prev => ({ ...prev, [key]: true }));
    try {
      await apiPost("/api/admin/backup/restore", {
        drive_file_id: file.drive_file_id,
        filename: file.name,
      });
      showToast({ message: "Database restored successfully.", type: "success" });
    } catch (e: any) {
      showToast({ message: e.message || "Restore command failed.", type: "error" });
    } finally {
      setRestoring(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDelete = async (file: DriveFile) => {
    setDialogState({ type: null });
    const key = fileKey(file);
    if (deleting[key]) return;
    setDeleting(prev => ({ ...prev, [key]: true }));
    try {
      await apiPost("/api/admin/backup/delete", {
        drive_file_id: file.drive_file_id,
        filename: file.name,
      });
      showToast({ message: "Backup deleted.", type: "success" });
      loadFiles();
    } catch (e: any) {
      showToast({ message: e.message || "Failed to delete backup.", type: "error" });
    } finally {
      setDeleting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDownloadConfirm = () => {
    if (dialogState.file) {
      const key = fileKey(dialogState.file);
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
    setDialogState({ type: null });
  };

  const formatSize = (bytes: number) => {
    const suffixes = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    let value = bytes;
    while (value >= 1024 && i < suffixes.length - 1) {
      value /= 1024;
      i++;
    }
    return `${value.toPrecision(3)} ${suffixes[i]}`;
  };

  const formatDate = (iso: string) => {
    if (!iso) return "—";
    return formatLocalizedDate(new Date(iso), {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="apexo-backups-container">
      <div className="apexo-expander">
        <div className="apexo-expander-header">
          <span className="icon">📁</span>
          <span>{t("backup_title")}</span>
        </div>
        
        <div className="apexo-expander-content">
          <div className="apexo-policy-panel">
            <div className="apexo-policy-header">
              <h4>{t("backup_auto_policy")}</h4>
              <button
                className="apexo-btn outline"
                type="button"
                onClick={handleSavePolicy}
                disabled={savingPolicy || loadingPolicy}
              >
                {savingPolicy ? t("backup_saving_policy") : t("backup_save_policy")}
              </button>
            </div>

            <div className="apexo-policy-grid">
              <label className="apexo-policy-field checkbox">
                <input
                  type="checkbox"
                  checked={policy.enabled}
                  onChange={(e) =>
                    setPolicy((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                />
                <span>{t("backup_enable_scheduler")}</span>
              </label>

              <label className="apexo-policy-field checkbox">
                <input
                  type="checkbox"
                  checked={policy.upload_to_drive}
                  onChange={(e) =>
                    setPolicy((prev) => ({
                      ...prev,
                      upload_to_drive: e.target.checked,
                    }))
                  }
                />
                <span>{t("backup_upload_drive")}</span>
              </label>

              <label className="apexo-policy-field">
                <span>{t("backup_frequency")}</span>
                <select
                  value={policy.frequency}
                  onChange={(e) =>
                    setPolicy((prev) => ({
                      ...prev,
                      frequency: e.target.value as BackupFrequency,
                    }))
                  }
                >
                  <option value="daily">{t("backup_frequency_daily")}</option>
                  <option value="weekly">{t("backup_frequency_weekly")}</option>
                  <option value="biweekly">{t("backup_frequency_biweekly")}</option>
                  <option value="monthly">{t("backup_frequency_monthly")}</option>
                </select>
              </label>

              <label className="apexo-policy-field">
                <span>{t("backup_run_hour_utc")}</span>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={policy.run_hour_utc}
                  onChange={(e) =>
                    setPolicy((prev) => ({
                      ...prev,
                      run_hour_utc: Number(e.target.value),
                    }))
                  }
                />
              </label>

              <label className="apexo-policy-field">
                <span>{t("backup_run_minute_utc")}</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={policy.run_minute_utc}
                  onChange={(e) =>
                    setPolicy((prev) => ({
                      ...prev,
                      run_minute_utc: Number(e.target.value),
                    }))
                  }
                />
              </label>

              <label className="apexo-policy-field">
                <span>{t("backup_retention_days")}</span>
                <input
                  type="number"
                  min={1}
                  value={policy.keep_days}
                  onChange={(e) =>
                    setPolicy((prev) => ({
                      ...prev,
                      keep_days: Number(e.target.value),
                    }))
                  }
                />
              </label>

              <label className="apexo-policy-field">
                <span>{t("backup_max_backups")}</span>
                <input
                  type="number"
                  min={1}
                  value={policy.keep_max}
                  onChange={(e) =>
                    setPolicy((prev) => ({
                      ...prev,
                      keep_max: Number(e.target.value),
                    }))
                  }
                />
              </label>
            </div>

            <div className="apexo-policy-meta">
              <span>{t("backup_next_run")}: {formatDate(policy.next_run_at || "")}</span>
              <span>{t("backup_last_run")}: {formatDate(policy.last_run_at || "")}</span>
            </div>
          </div>

          <div className="apexo-backup-list">
            {files.map(f => (
              <div key={fileKey(f)} className="apexo-backup-tile">
                <div className="apexo-tile-main">
                  <div className="apexo-tile-title">{formatDate(f.created)}</div>
                  <div className="apexo-tile-subtitle">{f.name}</div>
                </div>
                
                <div className="apexo-tile-actions">
                  <div className="apexo-file-size-badge">{formatSize(f.size_bytes)}</div>
                  
                  <button 
                    className="apexo-icon-btn" 
                    title="Download"
                    onClick={() => {
                      const key = fileKey(f);
                      if (!downloading[key]) {
                        setDownloading(prev => ({ ...prev, [key]: true }));
                        setDialogState({ type: 'download', file: f });
                      }
                    }}
                    disabled={downloading[fileKey(f)]}
                  >
                    {downloading[fileKey(f)] ? '⏳' : '⬇️'}
                  </button>
                  
                  <button 
                    className="apexo-icon-btn text-danger" 
                    title="Delete"
                    onClick={() => {
                      if (!deleting[fileKey(f)]) setDialogState({ type: 'delete', file: f });
                    }}
                    disabled={deleting[fileKey(f)]}
                  >
                    {deleting[fileKey(f)] ? '⏳' : '🗑️'}
                  </button>

                  <button 
                    className="apexo-icon-btn text-warning" 
                    title="Restore Backup"
                    onClick={() => {
                      if (!restoring[fileKey(f)]) setDialogState({ type: 'restore', file: f });
                    }}
                    disabled={restoring[fileKey(f)]}
                  >
                    {restoring[fileKey(f)] ? '⏳' : '🔄'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="apexo-bottom-controls">
            <div className="apexo-left-controls">
              <button 
                className={`apexo-btn ${creating ? 'loading' : ''}`} 
                onClick={handleCreateNew}
                disabled={creating}
              >
                ➕ Create New
              </button>
              <button 
                className={`apexo-btn ${uploading ? 'loading' : ''}`} 
                onClick={handleUploadClick}
                disabled={uploading}
              >
                ⬆️ Upload
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                accept=".dump,.sql,.gz,.zip" 
                onChange={handleFileChange} 
              />
            </div>
            
            <button 
              className={`apexo-icon-btn apexo-refresh-btn ${loading ? 'rotating' : ''}`}
              title="Refresh"
              onClick={() => {
                void loadFiles();
                void loadPolicy();
              }}
              disabled={loading || loadingPolicy}
            >
              🔄
            </button>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {dialogState.type === 'restore' && dialogState.file && (
        <div className="apexo-dialog-overlay">
          <div className="apexo-dialog">
            <h3 className="apexo-dialog-title">Restore Backup</h3>
            <div className="apexo-dialog-content">
              Are you sure you want to restore the database to this state? 
              <br/><br/>
              <b>({formatDate(dialogState.file.created)})</b>
              <br/><br/>
              Warning: This will overwrite current data.
            </div>
            <div className="apexo-dialog-actions">
              <button className="apexo-btn outline" onClick={() => setDialogState({ type: null })}>Cancel</button>
              <button className="apexo-btn danger" onClick={() => handleRestore(dialogState.file!)}>Restore</button>
            </div>
          </div>
        </div>
      )}

      {dialogState.type === 'delete' && dialogState.file && (
        <div className="apexo-dialog-overlay">
          <div className="apexo-dialog">
            <h3 className="apexo-dialog-title">Delete</h3>
            <div className="apexo-dialog-content">
              Are you sure you want to delete backup: <br/>
              <b>'{dialogState.file.name}'</b>?
              <br/>
              Backup Date: {formatDate(dialogState.file.created)}
            </div>
            <div className="apexo-dialog-actions">
              <button className="apexo-btn outline" onClick={() => setDialogState({ type: null })}>Cancel</button>
              <button className="apexo-btn danger" onClick={() => handleDelete(dialogState.file!)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {dialogState.type === 'download' && dialogState.file && (
        <div className="apexo-dialog-overlay">
          <div className="apexo-dialog">
            <h3 className="apexo-dialog-title">Download</h3>
            <div className="apexo-dialog-content">
              Use the following link to download the backup:
              <br/><br/>
              <input 
                type="text" 
                readOnly 
                value={dialogState.file.drive_link || "Link not generated"} 
                className="apexo-text-field"
                onClick={e => e.currentTarget.select()}
              />
            </div>
            <div className="apexo-dialog-actions">
              {dialogState.file.drive_link && (
                <a href={dialogState.file.drive_link} target="_blank" rel="noreferrer" className="apexo-btn primary" onClick={handleDownloadConfirm} style={{ textDecoration: 'none' }}>
                  Open Link
                </a>
              )}
              <button className="apexo-btn outline" onClick={handleDownloadConfirm}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
